import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, BookOpen, Landmark, FileText, Scale, Info } from "lucide-react";
import ReassuranceLine from "@/components/ReassuranceLine";
import { FADE_INITIAL, FADE_IN_VIEW, VIEWPORT_ONCE } from "@/lib/motion";

const RESOURCES = [
  {
    icon: Landmark,
    title: "Texas Courts — Self-Help",
    body: "Official self-help resources, forms, and court information from the Texas Judicial Branch.",
    href: "https://www.txcourts.gov/programs-services/self-help/",
  },
  {
    icon: FileText,
    title: "TexasLawHelp.org",
    body: "Free legal information, self-help forms, and toolkits for eviction, debt, and civil matters in Texas.",
    href: "https://texaslawhelp.org/",
  },
  {
    icon: Scale,
    title: "Texas Rule 145 (Fee Waiver)",
    body: "Information on the Statement of Inability to Afford Payment of Court Costs used across Texas courts.",
    href: "https://texaslawhelp.org/article/statement-of-inability-to-afford-payment-of-court-costs",
  },
  {
    icon: BookOpen,
    title: "Consumer Financial Protection Bureau",
    body: "Federal guidance on debt collection, your rights, and how to respond to a debt lawsuit.",
    href: "https://www.consumerfinance.gov/consumer-tools/debt-collection/",
  },
];

export default function Resources() {
  return (
    <div className="bg-white">
      <section className="bg-[#0B1B3D]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="section-label text-[#D4AF37]">Consumer &amp; Legal Resources</span>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Public self-help resources for self-represented individuals.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">
            The links below point to public self-help materials. You decide what applies to your
            situation — we simply prepare the document you direct.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 pt-10 sm:px-6 lg:px-8">
        <ReassuranceLine testId="resources-reassurance" />
      </div>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-8 md:grid-cols-2">
          {RESOURCES.map((r, i) => (
            <motion.a
              key={r.title}
              href={r.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`resource-link-${i}`}
              initial={FADE_INITIAL}
              whileInView={FADE_IN_VIEW}
              viewport={VIEWPORT_ONCE}
              transition={{ duration: 0.45, delay: (i % 2) * 0.06 }}
              className="group flex flex-col rounded-lg border border-slate-200 bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#0B1B3D]">
                  <r.icon className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <ExternalLink className="h-4 w-4 text-slate-400 transition-colors group-hover:text-[#D4AF37]" />
              </div>
              <h3 className="mt-5 font-serif text-xl font-semibold text-[#0B1B3D]">{r.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{r.body}</p>
            </motion.a>
          ))}
        </div>

        <div className="mt-12 flex items-start gap-3 rounded-lg border border-slate-200 bg-[#F8FAFC] p-6 text-sm leading-relaxed text-slate-600">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#D4AF37]" />
          <p>
            These resources are provided for your convenience only. Draft &amp; Defend does not
            select, interpret, or recommend what applies to your situation, and we do not endorse or
            control the content of external websites. Always confirm court-specific rules, deadlines,
            and filing procedures with your court.
          </p>
        </div>
      </section>
    </div>
  );
}
